<template>
    <div>
        <h1 class="display-heading">Prescription Detail</h1>
        <div class="card mb-3">
            <div class="card-body">
                <prescription-summary :prescriptionId="prescriptionDetails.prescriptionId"
                    :doctorName="prescriptionDetails.doctorName"
                    :patientName="prescriptionDetails.patientName"
                    :prescriptionDate="prescriptionDetails.prescriptionDate"
                    :prescriptionValidUntil="prescriptionDetails.prescriptionValidUntil"
                    :prescriptionSpecialReminder="prescriptionDetails.prescriptionSpecialReminder"
                    :prescriptionType="prescriptionDetails.prescriptionType"
                    :prescriptionStatus="prescriptionDetails.prescriptionStatus"
                    :isUploadedPrescription="uploadedPrescription.isUpload"
                    :uploadedFileId="uploadedPrescription.uploadFileId"
                    :uploadRemarks="uploadedPrescription.uploadRemarks"
                    :isDelivered="isPrescriptionDelivered"
                    :deliveryProvider="prescriptionDelivery.deliveryProvider"
                    :deliveryTrackingCode="prescriptionDelivery.trackingCode"
                    :deliveryTrackingWebAddress="prescriptionDelivery.trackingUrl"
                    :attachedDeliveryPhoto="prescriptionDelivery.trackingAttachment"
                    :isPatient="true">
                </prescription-summary>
            </div>
        </div>

        <div v-if="isAwaitingPayment && isTeleConsultationPrescription" class="card mb-3">
            <div class="card-header">Payment Details</div>
            <div class="card-body">
                <div v-if="!paymentSummary.isPaid && paymentDetails.companyCreditAutopayError && paymentDetails.companyCreditAutopayErrorMessage"
                    class="card mb-3">
                    <div class="card-body">
                        {{paymentDetails.companyCreditAutopayErrorMessage}}
                    </div>
                </div>
                <payment-detail ref="paymentDetail"
                    :paymentDetails="paymentDetails"
                    :paymentSummary="paymentSummary">
                    <button v-if="isAwaitingPayment" class="btn btn-primary btn-primary-insv" @click="showPaymentDisclaimer">Make Payment</button>
                </payment-detail>
            </div>
        </div>

        <div class="card mb-3">
            <div class="card-header">Prescription Details</div>
             <div class="card-body">
                <prescription-detail :isEdit="false" v-model="prescriptionList"></prescription-detail>
            </div>
        </div>

        <popup-modal ref="popupModalConfirmation" :hasCloseButton="false" title="Disclaimer">
           <template slot="content">
               <p>Please understand and agree to all of our <a :href="termsAndConditionPatientsUrl" target="_blank">Terms & Conditions</a> before proceeding.</p>
           </template>
           <template slot="footer">
                <button type="button" class="btn btn-primary btn-primary-insv" @click="disclaimerProceed">Agreed & Proceed</button>
                <button type="button" class="btn btn-secondary" @click="disclaimerCancel">Cancel</button>
           </template>
        </popup-modal>

        <popup-modal ref="popupPaymentStatus" :hasCloseButton="true" :title="paymentPrompt.paymentStatusTitle">
           <template slot="content">
               <p>{{paymentPrompt.paymentStatusDetails}}</p>
           </template>
           <template slot="footer">
               <button @click="dismissPaymentStatus" class="btn btn-primary btn-primary-insv">Okay</button>
           </template>
        </popup-modal>
    </div>
</template>

<script>
import {
    constructPrescriptionList
} from '@/utilities';
import prescriptionDetail from '@/components/prescription';
import prescriptionSummary from '@/components/prescription/summary';
import paymentDetail from '@/components/payment-detail';
import popupModal from '@/components/popup-modal';
export default {
    'name': 'PrescriptionDetailUser',
    'components': {
        prescriptionSummary,
        paymentDetail,
        prescriptionDetail,
        popupModal
    },
    'computed': {
        prescriptionId () {
            return this.$route.params.id;
        },
        isAwaitingPayment () {
            return this.prescriptionStatusId === 998;
        },
        isTeleConsultationPrescription () {
            return this.prescriptionDetails.isClinicPrescription === 0 && this.prescriptionDetails.isClinicConsultation === 0;
        },
        isPrescriptionDelivered () {
            return this.prescriptionStatusId === 4 || this.prescriptionStatusId === 100;
        },
        termsAndConditionPatientsUrl () {
            return this.$store.state.operator.termsAndConditionPatientsUrl;
        },
        isFamily () {
            return this.$route.name === 'family-prescription-detail';
        },
        getApi () {
            if (this.isFamily) {
                return 'Prescription/GetFamilyPrescription';
            }
            return 'Prescription/GetMyPrescription';
        }
    },
    data () {
        return {
            'paymentDetails': {
                'paymentIdShort': null,
                'completedDate': null,
                'paymentRemarks': null,
                'companyCreditAutopayError': false,
                'companyCreditAutopayErrorMessage': null
            },
            'paymentSummary': {
                'isPaid': false,
                'paymentSummaryId': '',
                'paymentAmount': 0,
                'summaryItems': []
            },
            'prescriptionDetails': {
                'prescriptionId': '',
                'doctorName': '',
                'patientName': '',
                'prescriptionDate': '',
                'prescriptionType': '',
                'prescriptionStatus': 0,
                'prescriptionValidUntil': '',
                'prescriptionSpecialReminder': '',
                'isClinicPrescription': 0,
                'isClinicConsultation': 0
            },
            'prescriptionDelivery': {
                'trackingCode': null,
                'deliveryProvider': null,
                'trackingAttachment': null,
                'trackingUrl': null
            },
            'uploadedPrescription': {
                'isUpload': false,
                'uploadFileId': '',
                'uploadRemarks': ''
            },
            'prescriptionList': [],
            'prescriptionStatusId': 0,
            'paymentPrompt': {
                'paymentStatusTitle': '',
                'paymentStatusDetails': ''
            }
        };
    },
    mounted () {
        this.axios.get(this.getApi, {
            'params': {
                'prescriptionId': this.prescriptionId
            }
        }).then(({ data }) => {
            let prescription = data.data;
            this.prescriptionDetails.prescriptionDate = prescription.cd;
            this.prescriptionDetails.prescriptionId = prescription.plis;
            this.prescriptionDetails.doctorName = prescription.dn;
            this.prescriptionDetails.patientName = prescription.pn;
            // this.prescriptionDetails.prescriptionType = prescription.iu ? 'User Uploaded Prescription' : 'Doctor Consultation';
            this.prescriptionDetails.prescriptionStatus = prescription.ps;
            this.prescriptionDetails.prescriptionValidUntil = prescription.vu;
            this.prescriptionDetails.prescriptionSpecialReminder = prescription.sr;
            this.prescriptionDetails.isClinicPrescription = prescription.icp;
            this.prescriptionDetails.isClinicConsultation = prescription.icc;

            this.prescriptionStatusId = prescription.ps;

            // set for delivery status display
            this.prescriptionDelivery.trackingCode = prescription.dtc;
            this.prescriptionDelivery.deliveryProvider = prescription.dp;
            this.prescriptionDelivery.trackingAttachment = prescription.dai;
            this.prescriptionDelivery.trackingUrl = prescription.dtu;

            // Set prescription type
            if (prescription.iu) {
                this.prescriptionDetails.prescriptionType = 'User Uploaded Prescription';
            } else if (prescription.icp) {
                this.prescriptionDetails.prescriptionType = 'Clinic Medicine Purchase';
            } else if (prescription.icc) {
                this.prescriptionDetails.prescriptionType = 'Clinic Doctor Consultation';
            } else {
                this.prescriptionDetails.prescriptionType = 'Telemedicine Doctor Consultation';
            }

            if (this.prescriptionDelivery.trackingUrl) {
                if (!/^https?:\/\//i.test(this.prescriptionDelivery.trackingUrl)) {
                    this.prescriptionDelivery.trackingUrl = 'http://' + this.prescriptionDelivery.trackingUrl;
                }
            }

            // uploaded
            this.initializePrescriptionUpload(prescription.iu, prescription.pu);

            this.paymentDetails.paymentRemarks = `Payment for Prescription #${this.prescriptionDetails.prescriptionId}.`;
            if (prescription.psr) {
                this.initializePaymentDetails(prescription.psr);
                this.initializePaymentSummary(prescription.psr);
            }

            if (prescription.pil) {
                this.prescriptionList = constructPrescriptionList(prescription.pil);
            }

            if (this.$route.query && this.$route.query.status) {
                if (this.$route.query.status === '1') {
                    this.paymentPrompt.paymentStatusTitle = 'Payment Successful!';
                    this.paymentPrompt.paymentStatusDetails = 'Thank you, your payment has been successfully completed! You are expected to receive your medicine(s) soon.';
                } else if (this.$route.query.status === '6') {
                    this.paymentPrompt.paymentStatusTitle = 'Payment Pending.';
                    this.paymentPrompt.paymentStatusDetails = 'Thank you, your payment status is pending.';
                } else {
                    this.paymentPrompt.paymentStatusTitle = 'Payment Unsuccessful';
                    this.paymentPrompt.paymentStatusDetails = 'Sorry, your payment was unsuccessful. Please try again.';
                }

                this.$refs.popupPaymentStatus.show();
            }
        });
    },
    'methods': {
        initializePaymentDetails (paymentSummary) {
            this.paymentDetails.paymentIdShort = paymentSummary.psids;
            this.paymentDetails.completedDate = paymentSummary.cd;

            let companyCreditFailedRelatedCodes = [3, 4, 5];
            this.paymentDetails.companyCreditAutopayError = paymentSummary.ccaf && companyCreditFailedRelatedCodes.includes(paymentSummary.ccafc);

            if (this.paymentDetails.companyCreditAutopayError) {
                switch (paymentSummary.ccafc) {
                case 3:
                    this.paymentDetails.companyCreditAutopayErrorMessage = 'The required payment exceeding the set transaction limit given by your company, please pay either via FPX or credit card. Thereafter you can seek reimbursement from your employer later.';
                    break;
                case 4:
                    this.paymentDetails.companyCreditAutopayErrorMessage = 'The required payment exceeding the set annual limit given by your company, please pay either via FPX or credit card. Thereafter you can seek reimbursement from your employer later.';
                    break;
                case 5:
                    this.paymentDetails.companyCreditAutopayErrorMessage = "Due to your company's internal process, we are unable to approve the payment of this transaction. Please pay either via FPX or credit card. Thereafter you can seek reimbursement from your employer later.";
                    break;
                }
            }
        },
        initializePaymentSummary (paymentSummary) {
            this.paymentSummary.isPaid = paymentSummary.ip;
            this.paymentSummary.paymentSummaryId = paymentSummary.psid;
            this.paymentSummary.paymentAmount = paymentSummary.am;
            this.paymentSummary.summaryItems = paymentSummary.psi.map((item) => ({
                'itemName': item.in,
                'itemType': item.it,
                'itemDescription': item.id,
                'itemPrice': item.ip
            }));
        },
        initializePrescriptionUpload (hasUpload, prescriptionUpload) {
            this.uploadedPrescription.isUpload = hasUpload;
            this.uploadedPrescription.uploadFileId = prescriptionUpload ? prescriptionUpload.puid : '';
            this.uploadedPrescription.uploadRemarks = prescriptionUpload ? prescriptionUpload.ur : '';
        },
        showPaymentDisclaimer () {
            this.$refs.paymentDetail.validateOffsetWallet().then((isValid) => {
                if (isValid) {
                    this.$refs.popupModalConfirmation.show();
                }
            });
        },
        disclaimerProceed () {
            this.$refs.popupModalConfirmation.hide();
            this.$refs.paymentDetail.payNow();
        },
        disclaimerCancel () {
            this.$refs.popupModalConfirmation.hide();
        },
        dismissPaymentStatus () {
            this.$refs.popupPaymentStatus.hide();
        }
    }

};

</script>

<style>

</style>
